import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  ChangeEvent,
  FocusEvent,
} from "react";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";

type IconProps = { className?: string };

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  onBlur?: () => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  showChevron?: boolean;
  label?: string;
  labelClassName?: string;
  placeholderClassName?: string;
  triggerIconClassName?: string;
  selectedOptionContainerClassName?: string;
  error?: boolean;
  errorMessage?: string;
  errorClassName?: string;
  Icon?: React.FC<IconProps>;
  iconLeft?: boolean;
  iconClassName?: string;
  required?: boolean;
  inputClassName?: string;
  minTime?: string;
}

const pad = (n: number) => n.toString().padStart(2, "0");

const TIME_OPTIONS: string[] = (() => {
  const options: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      options.push(`${pad(h)}:${pad(m)}`);
    }
  }
  return options;
})();

function parseInput(raw: string): string | null {
  const cleaned = raw.replace(/[^\d:]/g, "");
  if (!cleaned) return null;

  if (cleaned.includes(":")) {
    const [h, m] = cleaned.split(":");
    const hours = parseInt(h || "0", 10);
    const minutes = parseInt(m || "0", 10);
    if (isNaN(hours) || isNaN(minutes)) return null;
    if (hours > 23 || minutes > 59) return null;
    return `${pad(hours)}:${pad(minutes)}`;
  }

  const digits = cleaned.replace(/[^\d]/g, "");
  const numericValue = parseInt(digits, 10);

  switch (digits.length) {
    case 1:
    case 2:
      if (numericValue > 23) return null;
      return `${pad(numericValue)}:00`;
    case 3: {
      const minutes = parseInt(digits.slice(-2), 10);
      const hours = parseInt(digits.slice(0, -2), 10);
      if (hours > 23 || minutes > 59) return null;
      return `${pad(hours)}:${pad(minutes)}`;
    }
    case 4: {
      const minutes = parseInt(digits.slice(-2), 10);
      const hours = parseInt(digits.slice(0, 2), 10);
      if (hours > 23 || minutes > 59) return null;
      return `${pad(hours)}:${pad(minutes)}`;
    }
    default:
      return null;
  }
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  onBlur,
  id,
  placeholder = "hh:mm",
  disabled = false,
  className = "",
  triggerClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  ariaLabel,
  ariaDescribedBy,
  showChevron = true,
  label,
  labelClassName = "",
  placeholderClassName = "",
  triggerIconClassName = "",
  selectedOptionContainerClassName = "",
  error = false,
  errorMessage,
  errorClassName = "",
  Icon,
  iconLeft,
  iconClassName = "",
  required = false,
  inputClassName = "",
  minTime,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [isTyping, setIsTyping] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dropdownId =
    id || `timepicker-${Math.random().toString(36).slice(2, 9)}`;
  const listboxId = `${dropdownId}-listbox`;
  const triggerId = `${dropdownId}-trigger`;
  const defaultAriaDescribedBy = `${dropdownId}-description`;
  const errorId = `${dropdownId}-error`;

  useEffect(() => {
    if (!isTyping) {
      const next = value || "";
      if (next !== inputValue) {
        setInputValue(next);
      }
    }
  }, [value, isTyping, inputValue]);

  const filteredOptions = useMemo(() => {
    const base = minTime
      ? TIME_OPTIONS.filter((t) => t >= minTime)
      : TIME_OPTIONS;
    if (!inputValue) return base;
    if (isTyping) {
      const matches = base.filter((t) =>
        t.toLowerCase().startsWith(inputValue.toLowerCase()),
      );
      const remaining = base.filter((t) => !matches.includes(t));
      return [...matches, ...remaining];
    } else {
      const exactMatch = base.find((t) => t === inputValue);
      const otherOptions = base.filter((t) => t !== inputValue);
      return exactMatch ? [exactMatch, ...otherOptions] : otherOptions;
    }
  }, [inputValue, isTyping, minTime]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setFocusedIndex(-1);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [disabled, isOpen]);

  const commit = useCallback(
    (raw: string) => {
      const parsed = parseInput(raw);
      if (parsed) {
        onChange(parsed);
        setInputValue(parsed);
      }
      setIsTyping(false);
    },
    [onChange],
  );

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (triggerRef.current?.contains(e.relatedTarget as Node)) return;
    if (inputValue.trim()) {
      commit(inputValue);
    } else {
      onChange("");
      setInputValue("");
      setIsTyping(false);
    }
    setIsOpen(false);
    onBlur?.();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!/^[0-9:]*$/.test(e.target.value)) return;
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsTyping(true);
    setIsOpen(true);

    if (!newValue) {
      onChange("");
      return;
    }

    const parsed = parseInput(newValue);
    if (parsed) {
      onChange(parsed);
    }
  };

  const handleOptionSelect = useCallback(
    (selectedTime: string, e?: React.MouseEvent | React.KeyboardEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setInputValue(selectedTime);
      onChange(selectedTime);
      setTimeout(() => {
        setIsTyping(false);
        setIsOpen(false);
        inputRef.current?.blur();
      }, 0);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case "Enter":
          event.preventDefault();
          if (isOpen && focusedIndex >= 0) {
            handleOptionSelect(filteredOptions[focusedIndex]);
          } else {
            commit(inputValue);
            setIsOpen(false);
            inputRef.current?.blur();
          }
          break;
        case "Escape":
          if (isOpen) {
            setIsOpen(false);
            setFocusedIndex(-1);
            inputRef.current?.focus();
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) => {
              const nextIndex =
                prev < filteredOptions.length - 1 ? prev + 1 : 0;
              return nextIndex;
            });
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex((prev) => {
              const nextIndex =
                prev > 0 ? prev - 1 : filteredOptions.length - 1;
              return nextIndex;
            });
          }
          break;
        case "Home":
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex(0);
          }
          break;
        case "End":
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex(filteredOptions.length - 1);
          }
          break;
      }
    },
    [
      disabled,
      isOpen,
      focusedIndex,
      filteredOptions,
      handleOptionSelect,
      commit,
      inputValue,
    ],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  const renderTrigger = () => {
    return (
      <div
        className={`flex items-center gap-[8px] justify-between w-full px-[8px] py-[7px] ${selectedOptionContainerClassName}`}
      >
        <div
          className={`flex items-center gap-[8px] w-full ${iconLeft ? "flex-row-reverse" : ""}`}
        >
          {Icon && <Icon className={iconClassName} />}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={handleChange}
            disabled={disabled}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0 pr-1 bg-transparent border-none outline-none ${
              inputValue
                ? "text-heading-text dark:text-heading-text-dk"
                : placeholderClassName ||
                  "placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
            } ${disabled ? "!cursor-not-allowed" : ""} ${inputClassName}`}
          />
        </div>
        {showChevron && (
          <ChevronDownIcon
            className={`w-[8px] h-[8px] shrink-0 text-heading-text dark:text-heading-text-dk transition-transform duration-400 ${triggerIconClassName} ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label
          htmlFor={triggerId}
          className={`flex items-center gap-[2px] font-normal text-verba-field-label dark:text-verba-field-label-dk mb-1 ${labelClassName}`}
        >
          <p>{label}</p>
          {required && (
            <span className="text-verba-red-alert dark:text-verba-red-alert-dk ml-1">
              *
            </span>
          )}
        </label>
      )}

      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel || "Select time"}
        aria-describedby={ariaDescribedBy || defaultAriaDescribedBy}
        tabIndex={disabled ? -1 : 0}
        className={`select-none cursor-pointer rounded-[8px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk transition-colors focus:outline-none
          ${disabled && "opacity-80 "}
          ${error && "border-verba-red-alert dark:border-verba-red-alert-dk"}
          ${triggerClassName}
        `}
        onClick={handleToggle}
      >
        {" "}
        {renderTrigger()}
      </div>

      {error && errorMessage && (
        <div
          id={errorId}
          role="alert"
          className={`mt-1 text-xxs text-verba-red-alert dark:text-verba-red-alert-dk ${errorClassName}`}
        >
          <span>{errorMessage}</span>
        </div>
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          aria-label="Time options"
          aria-activedescendant={
            focusedIndex >= 0
              ? `${dropdownId}-option-${focusedIndex}`
              : undefined
          }
          className={`
            absolute z-50 w-full overflow-y-auto ${visibleScrollbar(
              "[&::-webkit-scrollbar]:w-[8px]",
            )} backdrop-blur-[10px] 
            bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk 
            rounded-[8px] shadow-popper-blur-effect transition-all duration-200 ease-out
            mt-1 max-h-[200px]
            ${dropdownClassName}
          `}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((time, index) => {
              const isSelected = time === inputValue;
              const isFocused = focusedIndex === index;

              return (
                <div
                  key={time}
                  id={`${dropdownId}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  className={`
                    flex items-center justify-between cursor-pointer border-b border-border-gray dark:border-border-gray-dk 
                    last:border-b-0 transition-colors px-[14px] py-[10px] min-w-0
                    ${
                      isSelected
                        ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk font-medium"
                        : isFocused
                          ? "bg-hover-effect dark:bg-hover-effect-dk"
                          : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                    }
                    ${optionClassName}
                  `}
                  onMouseDown={(e) => handleOptionSelect(time, e)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  <div className="overflow-hidden flex-1 min-w-0 pr-5 select-none">
                    {time}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-md text-heading-text dark:text-heading-text-dk">
              No time options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimePicker;
